import { LogOut, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../lib/authStore.js';
import { usePlatformAuthStore } from '../../lib/platformAuthStore.js';

/**
 * Persistent top banner shown whenever the active tenant session was
 * minted via platform impersonation (PRD §7). Clicking it clears the
 * tenant session and navigates back to the platform-side detail page
 * the impersonation was launched from.
 *
 * Returns null when the session is a regular tenant login.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const impersonation = useAuthStore((s) => s.impersonation);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);

  if (!impersonation || !user || !tenant) return null;

  const onExit = () => {
    // Drop the impersonated tenant session — the platform refresh cookie
    // (path /api/v1/platform/auth) is unaffected, so the platform admin
    // is still signed in and lands back on the tenant detail page.
    useAuthStore.getState().clear();
    const platformSignedIn = !!usePlatformAuthStore.getState().accessToken;
    if (platformSignedIn) {
      navigate(`/platform/tenants/${tenant.id}`);
    } else {
      navigate('/platform/login');
    }
  };

  return (
    <div
      className="flex items-center gap-3 border-b-2 border-danger bg-danger/10 px-6 py-2 text-sm"
      role="status"
      aria-live="polite"
    >
      <UserCog className="h-4 w-4 flex-shrink-0 text-danger" aria-hidden />
      <span className="flex-1">
        {t('impersonation.banner', { user: user.email, tenant: tenant.name })}
      </span>
      <button
        type="button"
        onClick={onExit}
        className="inline-flex items-center gap-1.5 rounded-md bg-danger px-3 py-1 text-xs font-medium text-fg-inverse hover:opacity-90"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        {t('impersonation.exit')}
      </button>
    </div>
  );
}
